<template>
  <div>
    <div>
      <div
        v-if="!props.loading"
        class="centered-content"
      >
        <div
          v-if="totalItems == 0"
          class="content-box"
          data-test="no-donations-message"
        >
          <p class="blank-title">{{ noDonationsTitle }}</p>
          <p class="paragraph">{{ noDonationsMessage }}</p>
          <Button
            v-if="showBrowseButton"
            variant="outlined"
            @click="onBrowse"
            :raised="isHovered"
            @mouseover="isHovered = true"
            @mouseleave="isHovered = false"
            data-test="browse-button"
          >
            Browse Organizations
          </Button>
        </div>
      </div>

      <div
        v-if="totalItems != 0"
        class="options-bar justify-between items-center"
      >
        <div class="search-filters flex">
          <Filter
            :showStatusFilter="false"
            :searchPlaceholder="props.sortByEmployeeName ? 'Search Organizations, Employees' : 'Search Organizations'"
            :dateFilters="dateFilters"
            :selectedDate="selectedDate"
            :frequencyFilters="frequencyFilters"
            :selectedFrequency="selectedFrequency"
            :sortOptions="sortOptions"
            :selectedSort="selectedSort"
            @applyFilters="applyFilters"
            @resetFilters="resetFilters"
            @update:modelValue="updateSearchTerm"
          >
            <template #export-button>
              <Button
                data-cy="exportButton"
                variant="link"
                size="small"
                type="secondary"
                style="color: black; font-size: 0.875rem"
                :disabled="disableExport"
                @click="onExport"
                data-test="export-button"
              >
                <img
                  style="padding-right: 5px"
                  :src="Export"
                  :class="{ 'disabled-img': disableExport }"
                />
                Export
              </Button>
            </template>
          </Filter>
        </div>
      </div>

      <div v-if="props.loading">
        <Skeleton
          v-for="n in 5"
          :key="n"
          class="item-tile-skeleton"
        />
      </div>

      <div
        v-else-if="visibleItems.length == 0 && totalItems != 0"
        class="content-box"
      >
        <p class="blank-title">No results found.</p>
        <p class="paragraph">Try changing your search/filters to find the item you are looking for.</p>
      </div>
      <div
        v-for="(item, index) in visibleItems"
        :key="index"
        class="donation-tile"
        data-test="donation-tile"
      >
        <div class="reverse-mobile">
          <div
            class="data-holder-small font-14 weight-400 top-left"
            style="text-align: left"
          >
            <div>
              {{ filters.formatDate(item.enteredDate) }}
            </div>
            <div>
              {{ item.recurringDonation ? 'Recurring' : 'One-Time' }}
            </div>
          </div>
          <div class="data-holder-small font-20 weight-600 top-right amount">
            {{ filters.formatMoney(item.amount) }}
          </div>
          <div class="action-button">
            <ActionPicker
              class="w-5 whitespace-nowrap"
              :actions="menuStr"
              @resendReceipt="resendReceipt(item.transactions_id)"
              @removeEmployee="removeEmployee(item.transactions_id)"
              style="width: 20px"
            >
              <template #trigger>
                <Button
                  variant="link"
                  size="large"
                  label="..."
                  style="width: 10px; color: black"
                >
                  &hellip;
                </Button>
              </template>
            </ActionPicker>
          </div>
        </div>
        <div class="details-container">
          <div v-if="props.showEmployeeDetails">
            <!-- 5777 is the lookup_value_id for: Name Not Displayed Publicly -->
            <div
              v-if="item.anonymous === 5777 || item.anonymous == 986"
              class="data-holder-medium"
            >
              Anonymous
            </div>
            <div v-else>
              <div class="data-holder-medium">{{ item.employeeName }}</div>
              <div class="data-holder-medium">ID: {{ item.company_employeeID }}</div>
            </div>
          </div>
        </div>
        <div class="data-holder-medium">{{ item.organization?.organizationName }}</div>

        <div class="data-holder-small payment-info">
          <div
            v-if="item.last4"
            class="font-14 weight-400"
            style="display: flex; align-items: center"
          >
            <img
              style="padding-right: 5px"
              :src="paymentImageSource(item.processingInfo_Brand)"
              :alt="item.processingInfo_Brand"
            />
            Ending in {{ item.last4 }}
          </div>
          <div
            v-else-if="item.profileName != null && showProfileName"
            class="font-14 weight-400"
          >
            {{ item.profileName }}
          </div>
          <div
            v-else
            class="font-14 weight-400"
          ></div>
        </div>
      </div>
    </div>
    <div
      v-if="visibleItems.length > 0"
      class="pagination"
    >
      <Paginator
        :rows="10"
        :totalRecords="props.store.filteredDonations.length"
        :first="(page - 1) * 10"
        @page="onPageChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, watchEffect, onMounted } from 'vue';
import filters from '@/helpers/filters';
import useIsMobile from '@/composables/useIsMobile.js';
import Export from '@/assets/Export.svg';
import Visa from '@/assets/Visa.svg';
import ACH from '@/assets/ACH.svg';
import Mastercard from '@/assets/Mastercard.svg';
import Amex from '@/assets/Amex.svg';
import Discover from '@/assets/Discover.svg';
import ActionPicker from '@/components/ActionPicker.vue';
import Filter from '@/components/data/Filter.vue';
import { useSnackbarStore } from '@/stores/SnackbarStore.js';

const isHovered = ref(false);

const searchTerm = ref('');

const props = defineProps({
  items: Array,
  mainColor: String,
  loading: Boolean,
  onBrowse: Function,
  onExport: Function,
  store: Object,
  showProfileName: {
    type: Boolean,
    default: true,
  },
  showEmployeeDetails: {
    type: Boolean,
    default: true,
  },
  searchFields: {
    type: Array,
    default: () => [],
  },
  noDonationsTitle: String,
  noDonationsMessage: String,
  showBrowseButton: {
    type: Boolean,
    default: true,
  },
  sortByEmployeeName: {
    type: Boolean,
    default: false,
  },
  menuItems: {
    type: Array,
    default: () => [],
  },
});

const visibleItems = ref([]);
const page = ref(1);
const { isMobile } = useIsMobile();

const resetFiltersCalled = ref(0);

const selectedDate = ref(null); // Ensure this is an object
const dateFilters = ref([
  { label: 'All', value: 'all' },
  { label: 'Past 7 Days', value: 'past7Days' },
  { label: 'Past Month', value: 'pastMonth' },
  { label: 'Past Year', value: 'pastYear' },
]);

const selectedFrequency = ref(null);
const frequencyFilters = ref([
  { label: 'All', value: 'all' },
  { label: 'One-Time', value: 'oneTime' },
  { label: 'Recurring', value: 'recurring' },
]);

const selectedSort = ref(null);
const sortOptions = computed(() => {
  const options = [
    { label: 'Date: Newest', value: 'mostRecent' },
    { label: 'Date: Oldest', value: 'leastRecent' },
    { label: 'Amount: High to Low', value: 'amountHighToLow' },
    { label: 'Amount: Low to High', value: 'amountLowToHigh' },
    { label: 'Organization: A to Z', value: 'organizationAToZ' },
    { label: 'Organization: Z to A', value: 'organizationZToA' },
  ];

  if (props.sortByEmployeeName) {
    options.push(
      { label: 'Employee: A to Z', value: 'employeeAToZ' },
      { label: 'Employee: Z to A', value: 'employeeZToA' },
    );
  }

  return options;
});

const sortItems = (items, sortBy) => {
  switch (sortBy) {
    case 'mostRecent':
    case 'leastRecent':
      return filters.sortByDate(items, sortBy, 'mostRecent', 'leastRecent', 'enteredDate');
    case 'amountHighToLow':
    case 'amountLowToHigh':
      return filters.sortByAmount(items, sortBy, 'amountHighToLow', 'amountLowToHigh', 'amount');
    case 'organizationAToZ':
    case 'organizationZToA':
      return filters.sortByName(items, sortBy, 'organizationAToZ', 'organizationZToA', 'organization.organizationName');
    case 'employeeAToZ':
    case 'employeeZToA':
      return filters.sortByName(items, sortBy, 'employeeAToZ', 'employeeZToA', 'employeeName');
    default:
      return items;
  }
};

const filterByFrequency = (frequency) => {
  switch (frequency) {
    case 'oneTime':
      return props.items.filter((item) => item.recurringDonation === false);
    case 'recurring':
      return props.items.filter((item) => item.recurringDonation === true);
    default:
      return props.items;
  }
};

const filterByDate = (items, date) => {
  if (!Array.isArray(items)) {
    console.error('filterByDate: items is not an array', items);
    return [];
  }
  return filters.filterByDate(items, date);
};

const filterBySearchTerm = (items) => {
  return items.filter((item) => {
    return props.searchFields.some((field) => {
      const value = field.split('.').reduce((o, i) => (o ? o[i] : null), item);
      return value && value.toString().toLowerCase().includes(searchTerm.value.toLowerCase());
    });
  });
};

const paginateItems = (items) => {
  const start = (page.value - 1) * 10;
  const end = start + 10;
  return items.slice(start, end);
};

const totalPages = ref(0);

const applyFilters = (filters = {}) => {
  if (filters.selectedDate !== undefined) {
    selectedDate.value = filters.selectedDate;
  }
  if (filters.selectedFrequency !== undefined) {
    selectedFrequency.value = filters.selectedFrequency;
  }
  if (filters.selectedSort !== undefined) {
    selectedSort.value = filters.selectedSort;
  }

  let items = props.items;

  items = filterByFrequency(selectedFrequency.value?.value || 'all');

  items = filterByDate(items, selectedDate.value?.value || 'all');

  items = sortItems(items, selectedSort.value?.value || 'all');

  items = filterBySearchTerm(items);

  visibleItems.value = paginateItems(items);

  // Update the store data
  props.store.totalDonations = items.length;
  props.store.totalAmountDonated = items.reduce((acc, item) => acc + item.amount, 0);
  props.store.filteredDonations = items;

  // Update totalPages based on filtered items
  totalPages.value = Math.ceil(props.store.filteredDonations.length / 10);
};

const resetFilters = () => {
  selectedDate.value = null;
  selectedFrequency.value = null;
  selectedSort.value = null;
  searchTerm.value = '';
  resetFiltersCalled.value++;
};

watch(resetFiltersCalled, () => {
  applyFilters(props.store);
});

const filtersActive = ref(false);
const combinedFilters = computed(() => ({
  searchTerm: searchTerm.value,
  selectedDate: selectedDate.value?.value || 'all',
  selectedFrequency: selectedFrequency.value?.value || 'all',
  selectedSort: selectedSort.value?.value || 'all',
}));

watch(
  combinedFilters,
  () => {
    filtersActive.value =
      searchTerm.value !== '' ||
      selectedDate.value?.value !== 'all' ||
      selectedFrequency.value?.value !== 'all' ||
      (selectedSort.value && selectedSort.value !== 'mostRecent');
  },
  { deep: true },
);

watchEffect(() => {
  applyFilters(props.store);
});

const totalItems = computed(() => (props.items ? props.items.length : 0));

onMounted(() => {
  applyFilters(props.store);
});

const disableExport = computed(() => {
  return totalItems.value == 0;
});

const paymentImageSource = (type) => {
  switch (type) {
    case 'Visa':
      return Visa;
    case 'Checking Account':
      return ACH;
    case 'MasterCard':
      return Mastercard;
    case 'American Express':
    case 'Amex':
      return Amex;
    case 'Discover':
      return Discover;
    default:
      return '';
  }
};

const menuStr = computed(() => JSON.stringify(props.menuItems));

async function resendReceipt(transactions_id) {
  const data = await props.store.getSingleReceipt(transactions_id);
  if (data && data.status == 'success' && data.message) {
    useSnackbarStore().show(data.message, 'info');
  } else {
    useSnackbarStore().show('Failed to resend receipt.', 'error');
  }
}

async function removeEmployee(transactions_id) {
  try {
    await props.store.removeEmployeeDonation(transactions_id);
    useSnackbarStore().show('Employee donation removed successfully.', 'info');
    // Remove the item from props.items
    const index = props.items.findIndex((item) => item.transactions_id === transactions_id);
    if (index !== -1) {
      props.items.splice(index, 1);
    }
    applyFilters(props.store);
  } catch (e) {
    useSnackbarStore().show('Failed to remove employee donation.', 'error');
  }
}

const updateSearchTerm = (newSearchTerm) => {
  searchTerm.value = newSearchTerm;
  applyFilters(props.store);
};

const onPageChange = (event) => {
  page.value = event.page + 1;
  applyFilters(props.store);
};
</script>

<style lang="scss" scoped>
.blank-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 20px;
}

.button {
  margin: 0 auto 20px;
}

.list-donation {
  max-width: 930px;
}
.totals {
  display: flex;
  justify-content: space-evenly;
  flex-direction: row;
  flex-wrap: wrap;
}
.disabled-img {
  opacity: 0.5;
}
.donation-tile {
  height: 96px;
  width: 930px;
  background-color: white;
  margin: 20px 0;
  align-items: center;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  padding: 16px 0;
  position: relative;
}
.item-tile-skeleton {
  height: 96px;
  width: 100%;
  background-color: #f0f0f0;
  margin: 20px 0;
  border-radius: 8px;
}
.data-holder-small {
  width: 150px;
}
.data-holder-medium {
  display: flex;
  width: 150px;
}
.data-holder-large {
  width: 300px;
  overflow: hidden;
}
.reverse-mobile {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.action-button {
  position: absolute;
  top: 0;
  right: -90px;
  width: 140px;
}
.pageNumber {
  padding: 10px;
}
.index {
  text-align: center;
  width: 20px;
  margin: 0 20px;
  line-height: 1.8rem;
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
}
a {
  text-decoration: none;
}

.pagination .pageNumber {
  margin: 0 5px;
  padding: 0 8px;
  border-radius: 3px;
  cursor: pointer;
}
.pagination .pageNumber:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.filters-sidebar {
  position: absolute;
  top: 0;
  right: 0;
  background-color: white;
  width: 250px;
  height: 100%;
  z-index: 1;
}
.options-bar {
  justify-content: space-between;
  width: 100%;
}
.filter {
  margin: 0 10px;
  z-index: 1;
}
.search-filters {
  align-items: center;
}
@media screen and (max-width: 1024px) {
  .options-bar {
    justify-content: center;
    flex-direction: column;
    align-items: center;
  }
  .donation-tile {
    padding: 16px;
    width: 90%;
    margin: 20px auto; // Center the donation tile and maintain space between tiles
  }
}
@media screen and (max-width: 1600px) {
  .data-holder-small {
    width: 130px;
  }
  .data-holder-medium {
    width: 150px;
  }
  .data-holder-large {
    width: 250px;
  }
  .list-donation {
    padding-bottom: 80px;
  }
}
@media screen and (max-width: 800px) {
  .data-holder-small {
    width: 100px;
  }
  .data-holder-medium {
    width: 130px;
  }
  .data-holder-large {
    width: 200px;
  }
  .list-donation {
    padding-bottom: 80px;
  }
}
@media screen and (max-width: 1024px) {
  .donation-tile {
    flex-direction: column;
    height: 168px;
    width: 100%; // Adjusted width for better mobile display
  }
  .action-button {
    top: 0px;
    right: -100px;
  }
  .data-holder-medium {
    width: 100%;
  }
  .data-holder-large {
    width: 100%;
  }
  .reverse-mobile {
    width: 100%;
    align-items: flex-start;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .search-filters {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0;
  }

  .list-donation {
    padding-bottom: 80px;
  }
  .top-left {
    text-align: left;
    width: auto;
    order: 1;
  }
  .top-right {
    text-align: right;
    width: auto;
    order: 2;
  }
  .details-container {
    text-align: left;
    width: 100%;
  }
  .payment-info {
    text-align: left;
    width: 100%;
  }
  .amount {
    order: 1;
    text-align: left;
    width: auto;
  }
}
@media screen and (max-width: 768px) {
  .options-bar {
    justify-content: center;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 10px;
    padding: 10px;
  }
  .search-filters {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0;
  }
  .filter {
    width: auto;
    margin: 5px 0;
  }
}

@media screen and (max-width: 624px) {
  .donation-tile {
    flex-direction: column;
    height: 168px;
    width: 100%; // Adjusted width for better mobile display
  }
  .action-button {
    top: 0px;
    right: -100px;
  }
  .data-holder-medium {
    width: 100%;
  }
  .data-holder-large {
    width: 100%;
  }
  .reverse-mobile {
    width: 100%;
    align-items: flex-start;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .search-filters {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0;
  }

  .list-donation {
    padding-bottom: 80px;
  }
  .top-left {
    text-align: left;
    width: auto;
    order: 1;
  }
  .top-right {
    text-align: right;
    width: auto;
    order: 2;
  }
  .details-container {
    text-align: left;
    width: 100%;
  }
  .payment-info {
    text-align: left;
    width: 100%;
  }
  .amount {
    order: 1;
    text-align: left;
    width: auto;
  }
}
@media (max-width: 420px) {
  .search-filters {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0;
  }
  .list-donation {
    padding-bottom: 80px;
  }
  .options-bar {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
  }
  .donation-tile {
    width: 100%; // Adjusted width for better mobile display
  }
}
</style>
